/* SERENIUM SECTIONS v2 — START
   Comportamiento de las secciones propias: escena activa de la pirámide
   olfativa, navegación del carril de campaña y cierre de la promoción. */

(() => {
  const SCENE_STORAGE_PREFIX = 'serenium:campaign-dismissed:';

  /* ---------- Pirámide olfativa ---------- */

  // Encoge el texto de una escena hasta que cabe en su caja, en vez de dejar
  // que la escena crezca. Baja el factor --se-fit por pasos y se detiene en
  // cuanto no desborda ni a lo alto ni a lo ancho.
  const FIT_MIN = 0.4;
  const FIT_STEP = 0.04;

  function fitScene(scene) {
    const content = scene.querySelector('.se-pyramid__scene-content');
    if (!content) return;

    const styles = getComputedStyle(scene);
    const available =
      scene.clientHeight - parseFloat(styles.paddingTop || '0') - parseFloat(styles.paddingBottom || '0');
    const overflows = () => content.offsetHeight > available + 1 || content.scrollWidth > content.clientWidth + 1;

    let fit = 1;
    scene.style.setProperty('--se-fit', '1');
    while (overflows() && fit > FIT_MIN) {
      fit = Math.max(FIT_MIN, fit - FIT_STEP);
      scene.style.setProperty('--se-fit', fit.toFixed(2));
    }
  }

  function initPyramid(root) {
    if (root.dataset.sePyramidReady === 'true') return;
    root.dataset.sePyramidReady = 'true';

    const scenes = Array.from(root.querySelectorAll('[data-se-scene]'));
    const dots = Array.from(root.querySelectorAll('[data-se-pyramid-dots] i'));
    if (scenes.length === 0) return;

    // Ajuste del texto: al cargar, al terminar de cargar las fuentes (cambian
    // las medidas) y cada vez que la sección cambia de tamaño.
    const fitAll = () => scenes.forEach(fitScene);
    let fitFrame = 0;
    const scheduleFit = () => {
      if (fitFrame) return;
      fitFrame = window.requestAnimationFrame(() => {
        fitFrame = 0;
        fitAll();
      });
    };

    fitAll();
    document.fonts?.ready.then(scheduleFit);
    if ('ResizeObserver' in window) {
      new ResizeObserver(scheduleFit).observe(root);
    } else {
      window.addEventListener('resize', scheduleFit, { passive: true });
    }

    const atmospheres = Array.from(
      new Set(scenes.map((scene) => scene.dataset.seAtmosphere).filter(Boolean))
    );

    const applyScene = (scene) => {
      const index = Number(scene.dataset.seScene);
      const atmosphere = scene.dataset.seAtmosphere;

      atmospheres.forEach((name) => {
        root.classList.toggle(`se-pyramid--${name}`, name === atmosphere);
      });

      scenes.forEach((item, i) => item.classList.toggle('is-active', i === index));
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    };

    if (!('IntersectionObserver' in window)) {
      applyScene(scenes[0]);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

        if (visible) applyScene(visible.target);
      },
      { threshold: [0.25, 0.5, 0.75], rootMargin: '-20% 0px -20% 0px' }
    );

    scenes.forEach((scene) => observer.observe(scene));
    applyScene(scenes[0]);
  }

  /* ---------- Carril de campaña ---------- */

  function initRail(root) {
    if (root.dataset.seRailReady === 'true') return;
    root.dataset.seRailReady = 'true';

    const rail = root.querySelector('[data-se-rail]');
    const prev = root.querySelector('[data-se-rail-prev]');
    const next = root.querySelector('[data-se-rail-next]');
    const close = root.querySelector('[data-se-rail-close]');

    if (rail) {
      const step = () => {
        const card = rail.querySelector('.se-campaign__card');
        return card ? card.offsetWidth + 1 : rail.clientWidth * 0.8;
      };

      prev?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
    }

    if (close) {
      const key = SCENE_STORAGE_PREFIX + (root.closest('.shopify-section')?.id || 'default');

      // En el editor siempre se muestra, para poder seguir configurándola.
      if (!window.Shopify?.designMode && sessionStorage.getItem(key) === '1') {
        root.hidden = true;
      }

      close.addEventListener('click', () => {
        root.hidden = true;
        try {
          sessionStorage.setItem(key, '1');
        } catch (error) {
          /* almacenamiento no disponible: se vuelve a mostrar al recargar */
        }
      });
    }
  }

  /* ---------- Catálogo ---------- */

  const SORT_ORDER = ['default', 'price-asc', 'price-desc'];
  const SORT_LABEL_KEYS = { default: 'labelDefault', 'price-asc': 'labelAsc', 'price-desc': 'labelDesc' };

  function parseCatalogItem(item) {
    return {
      id: item.dataset.productId,
      price: Number(item.dataset.price) || 0,
      index: Number(item.dataset.index) || 0,
      filters: {},
    };
  }

  function initCatalog(root) {
    if (root.dataset.seCatalogReady === 'true') return;
    root.dataset.seCatalogReady = 'true';

    const controller = new AbortController();
    const { signal } = controller;
    root.seAbort = () => controller.abort();

    const items = Array.from(root.querySelectorAll('[data-se-catalog-item]'));
    const data = new Map(items.map((item) => [item, parseCatalogItem(item)]));

    // Cada filtro por valores publica un mapa { idProducto: [valores] }.
    root.querySelectorAll('script[data-se-filter-values]').forEach((script) => {
      let map = {};
      try {
        map = JSON.parse(script.textContent || '{}');
      } catch (error) {
        return;
      }
      const filterId = script.dataset.seFilterValues;
      data.forEach((entry) => {
        entry.filters[filterId] = map[entry.id] || [];
      });
    });
    const tabs = Array.from(root.querySelectorAll('[data-se-tab]'));
    const toggles = Array.from(root.querySelectorAll('[data-se-panel-toggle]'));
    const panels = Array.from(root.querySelectorAll('[data-se-panel]'));
    const inputs = Array.from(root.querySelectorAll('[data-se-filter]'));
    const sortButton = root.querySelector('[data-se-sort]');
    const sortLabel = root.querySelector('[data-se-sort-label]');
    const counts = Array.from(root.querySelectorAll('[data-se-count]'));
    const countLabels = Array.from(root.querySelectorAll('[data-se-count-label]'));
    const empty = root.querySelector('[data-se-empty]');

    const state = { tabIds: null, selected: {}, maxPrice: {}, sort: 'default' };

    const matches = (entry) => {
      if (state.tabIds && !state.tabIds.has(entry.id)) return false;

      for (const [filterId, values] of Object.entries(state.selected)) {
        if (values.size === 0) continue;
        const productValues = entry.filters[filterId] || [];
        if (!productValues.some((value) => values.has(value))) return false;
      }

      for (const max of Object.values(state.maxPrice)) {
        if (max !== null && entry.price > max) return false;
      }

      return true;
    };

    const compare = (a, b) => {
      if (state.sort === 'price-asc') return a.price - b.price || a.index - b.index;
      if (state.sort === 'price-desc') return b.price - a.price || a.index - b.index;
      return a.index - b.index;
    };

    const apply = () => {
      let visible = 0;

      items.forEach((item) => {
        const show = matches(data.get(item));
        item.hidden = !show;
        if (show) visible += 1;
      });

      // Se reordena con `order` en vez de mover nodos: mover el DOM desconecta
      // y reconecta los componentes web de la tarjeta de producto del tema.
      [...items]
        .sort((a, b) => compare(data.get(a), data.get(b)))
        .forEach((item, position) => {
          item.style.order = String(position);
        });

      counts.forEach((element) => {
        element.textContent = String(visible);
      });
      countLabels.forEach((element) => {
        element.textContent = visible === 1 ? element.dataset.singular : element.dataset.plural;
      });
      if (empty) empty.hidden = visible !== 0;

      toggles.forEach((toggle) => {
        const filterId = toggle.dataset.seFilterId;
        const hasValues = (state.selected[filterId]?.size ?? 0) > 0;
        const hasPrice = (state.maxPrice[filterId] ?? null) !== null;
        toggle.classList.toggle('has-selection', hasValues || hasPrice);
      });
    };

    const openPanel = (panel) => {
      panels.forEach((item) => {
        item.hidden = item !== panel;
      });
      toggles.forEach((toggle) => {
        const expanded = panel !== null && toggle.getAttribute('aria-controls') === panel.id;
        toggle.setAttribute('aria-expanded', String(expanded));
      });
    };

    toggles.forEach((toggle) => {
      toggle.addEventListener(
        'click',
        () => {
          const panel = panels.find((item) => item.id === toggle.getAttribute('aria-controls'));
          openPanel(panel && panel.hidden ? panel : null);
        },
        { signal }
      );
    });

    document.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('[data-se-panel], [data-se-panel-toggle]')) return;
        openPanel(null);
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Escape') return;
        const open = panels.find((panel) => !panel.hidden);
        if (!open) return;
        openPanel(null);
        toggles.find((toggle) => toggle.getAttribute('aria-controls') === open.id)?.focus();
      },
      { signal }
    );

    tabs.forEach((tab) => {
      tab.addEventListener(
        'click',
        () => {
          tabs.forEach((item) => item.setAttribute('aria-pressed', String(item === tab)));
          const ids = tab.dataset.seTabIds;
          state.tabIds = ids === undefined ? null : new Set(ids.split(',').filter(Boolean));
          apply();
        },
        { signal }
      );
    });

    inputs.forEach((input) => {
      input.addEventListener(
        'change',
        () => {
          const filterId = input.dataset.seFilter;
          if (input.type === 'checkbox') {
            const checked = inputs.filter((item) => item.dataset.seFilter === filterId && item.checked);
            state.selected[filterId] = new Set(checked.map((item) => item.value));
          } else {
            state.maxPrice[filterId] = input.value === 'all' ? null : Number(input.value);
          }
          apply();
        },
        { signal }
      );
    });

    const reset = (includeTabs) => {
      state.selected = {};
      state.maxPrice = {};
      inputs.forEach((input) => {
        input.checked = input.type === 'radio' && input.value === 'all';
      });

      // La primera pestaña siempre es "todo el catálogo".
      if (includeTabs && tabs.length > 0) {
        state.tabIds = null;
        tabs.forEach((tab, index) => tab.setAttribute('aria-pressed', String(index === 0)));
      }

      openPanel(null);
      apply();
    };

    root.querySelectorAll('[data-se-reset]').forEach((button) => {
      button.addEventListener('click', () => reset(button.dataset.seReset === 'all'), { signal });
    });

    sortButton?.addEventListener(
      'click',
      () => {
        state.sort = SORT_ORDER[(SORT_ORDER.indexOf(state.sort) + 1) % SORT_ORDER.length];
        if (sortLabel) sortLabel.textContent = sortButton.dataset[SORT_LABEL_KEYS[state.sort]] || '';
        apply();
      },
      { signal }
    );
  }

  // El código del producto viaja dentro del título ("SH7 - Nombre - 100ml").
  // Se separa y se coloca junto al precio, en la fila inferior de la tarjeta.
  // Hay títulos con guion corto y otros con guion largo.
  const CODE_SEPARATORS = [' - ', ' \u2013 '];
  const CODE_MAX_LENGTH = 12;

  function moveProductCode(card) {
    // El enlace del título, no el de la galería: ambos usan class="contents".
    const titleLink = card.querySelector('[ref="productTitleLink"]');
    const row = card.querySelector('.product-card__content .group-block-content');
    if (!titleLink || !row || row.querySelector('.se-card-code')) return;

    // Si el bloque de SKU ya muestra el código, no se duplica.
    const sku = row.querySelector('.sku');
    if (sku && sku.textContent.trim() !== '') return;

    const titleNode = titleLink.querySelector('p') || titleLink;
    const title = titleNode.textContent.trim();
    const separator = CODE_SEPARATORS.find((item) => title.includes(item));
    if (!separator) return;

    const parts = title.split(separator);
    const code = parts[0].trim();
    if (!code || code.length > CODE_MAX_LENGTH || code.includes(' ')) return;

    titleNode.textContent = parts.slice(1).join(separator).trim();

    const badge = document.createElement('span');
    badge.className = 'se-card-code';
    badge.textContent = code;

    // A la izquierda, a la altura del precio.
    row.prepend(badge);
  }

  function init(scope = document) {
    scope.querySelectorAll('[data-se-pyramid]').forEach(initPyramid);
    scope.querySelectorAll('[data-se-rail-root]').forEach(initRail);
    scope.querySelectorAll('[data-se-catalog]').forEach(initCatalog);
    // Vale para cualquier rejilla del tema: inicio, colección o recomendaciones.
    scope.querySelectorAll('product-card').forEach(moveProductCode);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // El editor del tema vuelve a inyectar la sección al cambiar ajustes.
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  // Libera los listeners globales del catálogo cuando el editor retira la sección.
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-se-catalog]').forEach((root) => root.seAbort?.());
  });
})();

/* SERENIUM SECTIONS v2 — END */
